import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useOutletContext } from 'react-router-dom';

export const PLATFORM_META = Object.freeze({
    IOS: { label: 'iOS', store: 'App Store' },
    ANDROID: { label: 'Android', store: 'Google Play' }
});

export const EMPTY_SETTING = Object.freeze({
    latest_version: '',
    update_type: 'RECOMMENDED',
    popup_title: '',
    popup_message: '',
    store_url: '',
    enabled: false,
    updated_by: null,
    updated_at: null
});

const VERSION_PATTERN = /^\d{1,9}\.\d{1,9}(?:\.\d{1,9})?$/;

export function settingToForm(setting) {
    return {
        latest_version: setting?.latest_version || '',
        update_type: setting?.update_type || 'RECOMMENDED',
        popup_title: setting?.popup_title || '',
        popup_message: setting?.popup_message || '',
        store_url: setting?.store_url || '',
        enabled: Boolean(setting?.enabled),
        updated_by: setting?.updated_by || null,
        updated_at: setting?.updated_at || null
    };
}

export function compareDisplayVersions(left, right) {
    if (!VERSION_PATTERN.test(left) || !VERSION_PATTERN.test(right)) return null;
    const leftParts = left.split('.').map(Number);
    const rightParts = right.split('.').map(Number);
    while (leftParts.length < 3) leftParts.push(0);
    while (rightParts.length < 3) rightParts.push(0);
    for (let index = 0; index < 3; index += 1) {
        if (leftParts[index] < rightParts[index]) return -1;
        if (leftParts[index] > rightParts[index]) return 1;
    }
    return 0;
}

export function validateAppVersionForm(form) {
    if (!VERSION_PATTERN.test(form.latest_version.trim())) {
        return '최신 버전은 1.2 또는 1.2.0 같은 숫자 형식으로 입력해 주세요.';
    }
    if (!['RECOMMENDED', 'FORCED'].includes(form.update_type)) {
        return '업데이트 유형을 선택해 주세요.';
    }
    const title = form.popup_title.trim();
    if (!title || title.length > 120) return '팝업 제목은 1~120자로 입력해 주세요.';
    const message = form.popup_message.trim();
    if (!message || message.length > 1000) return '팝업 본문은 1~1,000자로 입력해 주세요.';
    try {
        if (form.store_url.trim().length > 1000) throw new Error('invalid');
        const storeUrl = new URL(form.store_url.trim());
        if (storeUrl.protocol !== 'https:' || !storeUrl.hostname) throw new Error('invalid');
    } catch (error) {
        return '스토어 주소는 https://로 시작하는 유효한 주소여야 합니다.';
    }
    return '';
}

function formatUpdatedAt(value) {
    if (!value) return '아직 수정 이력이 없습니다.';
    const parsed = Date.parse(value);
    if (!Number.isFinite(parsed)) return value;
    return new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).format(parsed);
}

function AppVersionSettings() {
    const { permissions, role } = useOutletContext() || {};
    const canManage = role === 'admin' || Boolean(permissions?.app_version_manage);
    const [settings, setSettings] = useState({});
    const [activePlatform, setActivePlatform] = useState('IOS');
    const [form, setForm] = useState(settingToForm());
    const [savedForm, setSavedForm] = useState(settingToForm());
    const [loading, setLoading] = useState(canManage);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const applySetting = (setting) => {
        const next = settingToForm(setting);
        setForm(next);
        setSavedForm(next);
    };

    const loadSettings = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/api/admin/app-versions');
            const byPlatform = Object.fromEntries(
                (response.data.settings || []).map((setting) => [setting.platform, setting])
            );
            setSettings(byPlatform);
            applySetting(byPlatform[activePlatform]);
        } catch (loadError) {
            setError(loadError.response?.data?.message || '앱 버전 설정을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (canManage) loadSettings();
        // 첫 진입 시 한 번만 조회하고 플랫폼 전환은 로컬 설정을 사용한다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManage]);

    const selectPlatform = (platform) => {
        setActivePlatform(platform);
        applySetting(settings[platform]);
        setError('');
        setNotice('');
    };

    const updateField = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
        setError('');
        setNotice('');
    };

    const save = async () => {
        const validationError = validateAppVersionForm(form);
        if (validationError) {
            setError(validationError);
            return;
        }
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const payload = {
                latest_version: form.latest_version.trim(),
                update_type: form.update_type,
                popup_title: form.popup_title.trim(),
                popup_message: form.popup_message.trim(),
                store_url: form.store_url.trim(),
                enabled: form.enabled
            };
            const response = await axios.put(`/api/admin/app-versions/${activePlatform}`, payload);
            const setting = response.data.setting;
            setSettings((current) => ({ ...current, [activePlatform]: setting }));
            applySetting(setting);
            setNotice(response.data.changed === false ? '변경된 내용이 없습니다.' : '앱 버전 정책을 저장했습니다.');
        } catch (saveError) {
            setError(saveError.response?.data?.message || '앱 버전 설정을 저장하지 못했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);
    const isDowngrade = useMemo(() => {
        if (!savedForm.latest_version || !form.latest_version) return false;
        return compareDisplayVersions(form.latest_version, savedForm.latest_version) === -1;
    }, [form.latest_version, savedForm.latest_version]);

    if (!canManage) {
        return <StatePanel role="alert">앱 버전 관리 권한이 필요합니다.</StatePanel>;
    }
    if (loading) return <StatePanel>앱 버전 설정을 불러오는 중입니다.</StatePanel>;

    const platformMeta = PLATFORM_META[activePlatform];
    const forced = form.update_type === 'FORCED';

    return (
        <PageShell>
            <Header>
                <div>
                    <Eyebrow>APP RELEASE CONTROL</Eyebrow>
                    <h1>앱 버전 관리</h1>
                    <p>플랫폼별 최신 버전과 업데이트 안내 방식을 설정합니다.</p>
                </div>
                <StatusPill $enabled={form.enabled}>{form.enabled ? '정책 활성' : '정책 비활성'}</StatusPill>
            </Header>

            <PlatformTabs aria-label="플랫폼 선택">
                {Object.entries(PLATFORM_META).map(([platform, meta]) => (
                    <PlatformTab
                        key={platform}
                        type="button"
                        $active={activePlatform === platform}
                        aria-pressed={activePlatform === platform}
                        onClick={() => selectPlatform(platform)}
                    >
                        <strong>{meta.label}</strong>
                        <span>{settings[platform]?.latest_version || '버전 미등록'}</span>
                    </PlatformTab>
                ))}
            </PlatformTabs>

            {error && <Message role="alert" $error>{error}</Message>}
            {notice && <Message role="status">{notice}</Message>}

            <ContentGrid>
                <SettingsCard>
                    <CardHeader>
                        <div>
                            <h2>{platformMeta.label} 정책 설정</h2>
                            <p>비활성 상태에서는 구버전 앱에도 업데이트 팝업이 표시되지 않습니다.</p>
                        </div>
                        <SwitchButton
                            type="button"
                            role="switch"
                            aria-label="버전 정책 활성화"
                            aria-checked={form.enabled}
                            $enabled={form.enabled}
                            onClick={() => updateField('enabled', !form.enabled)}
                        >
                            <span />
                        </SwitchButton>
                    </CardHeader>

                    <FieldGroup>
                        <label htmlFor="latest-version">최신 표시 버전</label>
                        <input
                            id="latest-version"
                            value={form.latest_version}
                            placeholder={activePlatform === 'IOS' ? '예: 7.1.0' : '예: 2.5.0'}
                            onChange={(event) => updateField('latest_version', event.target.value)}
                        />
                        <small>빌드 번호는 비교하지 않으며 7.0과 7.0.0은 같은 버전입니다.</small>
                        {isDowngrade && <InlineWarning>기존 저장 버전보다 낮습니다. 저장하면 업데이트 대상이 줄어듭니다.</InlineWarning>}
                    </FieldGroup>

                    <FieldGroup>
                        <label>업데이트 유형</label>
                        <ModeGrid>
                            <ModeButton
                                type="button"
                                $active={!forced}
                                onClick={() => updateField('update_type', 'RECOMMENDED')}
                            >
                                <strong>권장 업데이트</strong>
                                <span>업데이트 또는 나중에 선택</span>
                            </ModeButton>
                            <ModeButton
                                type="button"
                                $active={forced}
                                $danger
                                onClick={() => updateField('update_type', 'FORCED')}
                            >
                                <strong>강제 업데이트</strong>
                                <span>업데이트 전 로그인 차단</span>
                            </ModeButton>
                        </ModeGrid>
                        <PolicyWarning>
                            최신 정책은 플랫폼별 1건입니다. 강제에서 권장으로 바꾸면 모든 구버전도 권장 대상으로 바뀝니다.
                        </PolicyWarning>
                    </FieldGroup>

                    <FieldGroup>
                        <label htmlFor="popup-title">팝업 제목</label>
                        <input
                            id="popup-title"
                            maxLength={120}
                            value={form.popup_title}
                            onChange={(event) => updateField('popup_title', event.target.value)}
                        />
                        <small>{form.popup_title.length}/120</small>
                    </FieldGroup>

                    <FieldGroup>
                        <label htmlFor="popup-message">팝업 본문</label>
                        <textarea
                            id="popup-message"
                            maxLength={1000}
                            rows={5}
                            value={form.popup_message}
                            onChange={(event) => updateField('popup_message', event.target.value)}
                        />
                        <small>{form.popup_message.length}/1,000</small>
                    </FieldGroup>

                    <FieldGroup>
                        <label htmlFor="store-url">{platformMeta.store} 주소</label>
                        <input
                            id="store-url"
                            maxLength={1000}
                            value={form.store_url}
                            readOnly
                        />
                        <small>앱의 스토어 상세페이지 고정 주소입니다. 앱 버전 변경 시에는 수정하지 않습니다.</small>
                    </FieldGroup>

                    <Footer>
                        <div>
                            <span>{formatUpdatedAt(form.updated_at)}</span>
                            {form.updated_by && <span> · {form.updated_by}</span>}
                        </div>
                        <div>
                            <SecondaryButton type="button" disabled={!dirty || saving} onClick={() => setForm(savedForm)}>
                                되돌리기
                            </SecondaryButton>
                            <PrimaryButton type="button" disabled={saving} onClick={save}>
                                {saving ? '저장 중...' : '정책 저장'}
                            </PrimaryButton>
                        </div>
                    </Footer>
                </SettingsCard>

                <PreviewCard>
                    <PreviewHeader>
                        <span>{platformMeta.label} APP PREVIEW</span>
                        <strong>{form.latest_version || '버전 미입력'}</strong>
                    </PreviewHeader>
                    <PhoneFrame>
                        <AppBar>AirconRG</AppBar>
                        <Backdrop>
                            <PopupPreview $forced={forced}>
                                <Badge $forced={forced}>{forced ? '필수 업데이트' : '새 버전 안내'}</Badge>
                                <h3>{form.popup_title || '업데이트 안내 제목'}</h3>
                                <p>{form.popup_message || '관리자 화면에 입력한 안내 문구가 여기에 표시됩니다.'}</p>
                                <UpdateButton type="button">업데이트</UpdateButton>
                                {!forced && <LaterButton type="button">나중에</LaterButton>}
                            </PopupPreview>
                        </Backdrop>
                    </PhoneFrame>
                    <PreviewNote>
                        {forced
                            ? '닫기·뒤로가기를 차단하고 스토어 복귀 후 다시 확인합니다.'
                            : '앱을 새로 실행할 때마다 한 번 표시하고 나중에 선택할 수 있습니다.'}
                    </PreviewNote>
                </PreviewCard>
            </ContentGrid>
        </PageShell>
    );
}

export default AppVersionSettings;

const PageShell = styled.main`
    min-height: calc(100vh - 130px);
    padding: 46px clamp(28px, 5vw, 80px) 80px;
    background: #f5f7fa;
    color: #172033;
`;

const Header = styled.header`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 24px;
    h1 { margin: 4px 0 6px; font-size: 32px; letter-spacing: -0.04em; }
    p { margin: 0; color: #667085; }
`;

const Eyebrow = styled.span`
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    color: #16835f;
`;

const StatusPill = styled.span`
    padding: 8px 13px;
    border-radius: 999px;
    background: ${({ $enabled }) => $enabled ? '#dff6ec' : '#eceff3'};
    color: ${({ $enabled }) => $enabled ? '#087451' : '#667085'};
    font-size: 13px;
    font-weight: 700;
`;

const PlatformTabs = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 220px));
    gap: 10px;
    margin-bottom: 18px;
`;

const PlatformTab = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border: 1px solid ${({ $active }) => $active ? '#16835f' : '#d8dee8'};
    border-radius: 12px;
    background: ${({ $active }) => $active ? '#eaf8f2' : '#fff'};
    color: #172033;
    cursor: pointer;
    span { font-size: 12px; color: #667085; }
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(320px, 0.8fr);
    gap: 20px;
    align-items: start;
    @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

const SettingsCard = styled.section`
    background: #fff;
    border: 1px solid #e1e6ee;
    border-radius: 18px;
    box-shadow: 0 12px 32px rgba(29, 41, 57, 0.06);
    padding: 26px;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding-bottom: 22px;
    border-bottom: 1px solid #edf0f4;
    h2 { margin: 0 0 6px; font-size: 20px; }
    p { margin: 0; color: #667085; font-size: 13px; }
`;

const SwitchButton = styled.button`
    width: 48px;
    height: 27px;
    flex: 0 0 auto;
    border: 0;
    border-radius: 999px;
    padding: 3px;
    background: ${({ $enabled }) => $enabled ? '#16835f' : '#cbd2dc'};
    cursor: pointer;
    span {
        display: block;
        width: 21px;
        height: 21px;
        border-radius: 50%;
        background: #fff;
        transform: translateX(${({ $enabled }) => $enabled ? '21px' : '0'});
        transition: transform 0.18s ease;
    }
`;

const FieldGroup = styled.div`
    margin-top: 22px;
    label { display: block; margin-bottom: 8px; font-size: 13px; font-weight: 750; }
    input, textarea {
        width: 100%;
        box-sizing: border-box;
        border: 1px solid #d8dee8;
        border-radius: 10px;
        background: #fbfcfd;
        padding: 12px 13px;
        font: inherit;
        color: #172033;
        &:focus { outline: 2px solid rgba(22, 131, 95, 0.15); border-color: #16835f; }
    }
    textarea { resize: vertical; line-height: 1.55; }
    small { display: block; margin-top: 6px; color: #7a8494; font-size: 12px; }
`;

const ModeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
`;

const ModeButton = styled.button`
    text-align: left;
    padding: 15px;
    border: 1px solid ${({ $active, $danger }) => $active ? ($danger ? '#d65050' : '#16835f') : '#d8dee8'};
    border-radius: 12px;
    background: ${({ $active, $danger }) => $active ? ($danger ? '#fff1f1' : '#eaf8f2') : '#fff'};
    cursor: pointer;
    strong, span { display: block; }
    strong { margin-bottom: 4px; color: #172033; }
    span { color: #667085; font-size: 12px; }
`;

const PolicyWarning = styled.p`
    margin: 10px 0 0;
    padding: 10px 12px;
    border-radius: 9px;
    background: #fff7e8;
    color: #8a5a12;
    font-size: 12px;
    line-height: 1.5;
`;

const InlineWarning = styled.div`
    margin-top: 8px;
    color: #b54708;
    font-size: 12px;
    font-weight: 700;
`;

const Footer = styled.footer`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 26px;
    padding-top: 20px;
    border-top: 1px solid #edf0f4;
    color: #7a8494;
    font-size: 12px;
    > div:last-child { display: flex; gap: 8px; }
`;

const BaseButton = styled.button`
    border-radius: 9px;
    padding: 10px 16px;
    font-weight: 750;
    cursor: pointer;
    &:disabled { opacity: 0.48; cursor: default; }
`;

const PrimaryButton = styled(BaseButton)`
    border: 1px solid #16835f;
    background: #16835f;
    color: #fff;
`;

const SecondaryButton = styled(BaseButton)`
    border: 1px solid #d8dee8;
    background: #fff;
    color: #475467;
`;

const PreviewCard = styled.aside`
    overflow: hidden;
    border-radius: 18px;
    background: #172033;
    color: #fff;
    box-shadow: 0 18px 38px rgba(23, 32, 51, 0.18);
`;

const PreviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.11);
    span { color: #9da8ba; font-size: 10px; letter-spacing: 0.14em; }
`;

const PhoneFrame = styled.div`
    margin: 22px;
    overflow: hidden;
    border: 6px solid #0c1321;
    border-radius: 28px;
    background: #f2f4f7;
`;

const AppBar = styled.div`
    padding: 15px;
    background: #fff;
    color: #172033;
    text-align: center;
    font-weight: 800;
`;

const Backdrop = styled.div`
    min-height: 390px;
    display: grid;
    place-items: center;
    padding: 20px;
    background: linear-gradient(rgba(23, 32, 51, 0.56), rgba(23, 32, 51, 0.68)),
        linear-gradient(145deg, #e9f8f2 0%, #d6e8ff 100%);
`;

const PopupPreview = styled.div`
    width: 100%;
    box-sizing: border-box;
    border-top: 4px solid ${({ $forced }) => $forced ? '#d65050' : '#16835f'};
    border-radius: 14px;
    background: #fff;
    color: #172033;
    padding: 22px;
    box-shadow: 0 18px 40px rgba(12, 19, 33, 0.24);
    h3 { margin: 12px 0 8px; font-size: 19px; }
    p { min-height: 66px; margin: 0 0 20px; color: #667085; line-height: 1.55; white-space: pre-wrap; }
`;

const Badge = styled.span`
    display: inline-block;
    padding: 5px 8px;
    border-radius: 6px;
    background: ${({ $forced }) => $forced ? '#fff1f1' : '#eaf8f2'};
    color: ${({ $forced }) => $forced ? '#b42318' : '#087451'};
    font-size: 11px;
    font-weight: 800;
`;

const UpdateButton = styled.button`
    width: 100%;
    border: 0;
    border-radius: 9px;
    padding: 11px;
    background: #16835f;
    color: #fff;
    font-weight: 800;
`;

const LaterButton = styled.button`
    width: 100%;
    border: 0;
    padding: 10px 0 0;
    background: transparent;
    color: #667085;
`;

const PreviewNote = styled.p`
    margin: 0;
    padding: 0 22px 22px;
    color: #b8c1d0;
    font-size: 12px;
    line-height: 1.55;
`;

const Message = styled.div`
    margin-bottom: 14px;
    padding: 11px 14px;
    border-radius: 10px;
    background: ${({ $error }) => $error ? '#fff0f0' : '#eaf8f2'};
    color: ${({ $error }) => $error ? '#b42318' : '#087451'};
    font-size: 13px;
`;

const StatePanel = styled.div`
    margin: 60px;
    padding: 34px;
    border: 1px solid #e1e6ee;
    border-radius: 16px;
    background: #fff;
    text-align: center;
    color: #667085;
`;
